import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi, deviceApi, TOKEN_KEY, USER_KEY } from '@/services/api.js';
import { initEcho, tearDownEcho } from '@/services/echo.js';

const AuthContext = createContext(null);

/*
| Where each role lands after sign-in.
|
| platform_admin goes to /hub/settings rather than /hub. It is NOT a superset
| of hub_admin — the backend gates /api/v1/hub/* on `role:hub_admin` strictly,
| so a user holding only platform_admin would land on a dashboard whose every
| widget answers 403. Platform settings is the one surface that role can
| actually use. A user holding BOTH roles still lands on /hub, because
| ROLE_PRECEDENCE below ranks hub_admin higher.
*/
export const ROLE_HOMES = {
  // Both now load the hub dashboard — it was diverted to settings only
  // because /hub refused the admin tier. Settings is one click away in the nav.
  platform_admin:        '/hub',
  super_admin:           '/hub',
  citizen:               '/citizen',
  agency_officer:        '/agency',
  agency_supervisor:     '/agency',
  agency_admin:          '/agency',
  ifps_supervisor:       '/ifps',
  firl_admin:            '/firl',
  // Not /firl: that dashboard reads enforcement data, which the API restricts
  // to FIRL administrators. Casework is where a program officer works.
  program_officer:       '/firl/ffos',
  veris_admin:           '/veris',
  election_official:     '/veris',
  hub_admin:             '/hub',
  // The hub read routes now admit oversight, so this lands cleanly again.
  // Writes — the agency registry create and update — remain hub_admin.
  inspector_general:     '/hub',
  judge:                 '/judicial',
  magistrate:            '/judicial',
  prosecutor:            '/judicial',
  // Not /ifps: that dashboard reads escalations and disputes from the
  // supervisor-only admin block, so a partner landed on an error the moment
  // they signed in. /ifps/signals is their console.
  institutional_partner: '/ifps/signals',
};

/*
| Which home wins when a user holds several roles — and they routinely do:
| the bootstrapped admin holds both platform_admin and hub_admin.
|
| This used to be decided by whatever order the API happened to return roles
| in, which is not a guarantee Spatie makes. Ordering it explicitly means a
| multi-role user lands on the same surface every time, and lands on the
| richest one they can actually operate rather than the narrowest.
*/
const ROLE_PRECEDENCE = [
  // Operational surfaces first — these have real dashboards behind them.
  'hub_admin',
  'inspector_general',
  'veris_admin',
  'election_official',
  'firl_admin',
  'program_officer',
  'ifps_supervisor',
  'institutional_partner',
  'agency_admin',
  'agency_supervisor',
  'agency_officer',
  'judge',
  'magistrate',
  'prosecutor',
  // Admin tiers last: they are narrow by design, so they only decide the
  // destination for a user who holds nothing else.
  'platform_admin',
  'super_admin',
  'citizen',
];

/** First home among the user's roles, by the precedence above. */
export function homeForRoles(roles = []) {
  for (const role of ROLE_PRECEDENCE) {
    if (roles.includes(role) && ROLE_HOMES[role]) return ROLE_HOMES[role];
  }
  // Fall back to any mapped role not listed in the precedence table, so a
  // role added to ROLE_HOMES but not here still routes somewhere sensible.
  for (const role of roles) {
    if (ROLE_HOMES[role]) return ROLE_HOMES[role];
  }
  return '/';
}

/**
 * Register this browser with the Device Trust Authority.
 *
 * The `device.attested` middleware guards every `/rerr/citizen/*` and
 * `/vision/citizen/*` route: it looks up a Device row by the
 * X-Device-Fingerprint header we send and 403s (`DEVICE_UNKNOWN`) when there
 * isn't one. Registration never creates that row, so an un-attested web
 * session cannot submit a report, read its own report history, or manage an
 * enrolled camera or vehicle.
 *
 * Called after every successful token issuance. Deliberately non-fatal — a
 * failure here degrades the device-gated pages, but must not strand a user
 * who signed in correctly (agency / hub / VERIS surfaces don't need it).
 */
async function attestDevice() {
  try {
    await deviceApi.attest();
  } catch (err) {
    if (import.meta.env.DEV) console.warn('Device attestation failed:', err);
  }
}

function readUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Both /auth/login and /auth/me return roles + permissions as SIBLINGS
 * of `user`, not nested inside it:
 *   { user: { id, name, ... }, roles: [...], permissions: [...], token }
 * Flatten into one object so `user.roles` is always populated — otherwise
 * post-login routing (homeFor / ProtectedRoute) sees an empty role list
 * and bounces the user back to the public site.
 */
function normalizeUser(res) {
  const u = res?.user ?? res ?? {};
  return {
    ...u,
    roles:       res?.roles       ?? u.roles       ?? [],
    permissions: res?.permissions ?? u.permissions ?? [],
  };
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(() => readUser());
  const [loading, setLoading] = useState(false);

  // Validate the cached token against the BE on every mount. Prior
  // behaviour was "trust localStorage if both token + user are cached"
  // — but tokens expire (Sanctum 30-day default), and a stale-but-
  // present cache made AuthContext claim isAuthenticated=true, which
  // in turn made Login.jsx redirect to homeFor() the moment a visitor
  // hit /login. Symptom: "the login link doesn't bring the login
  // screen". Always call /auth/me when a token exists; clear cache on
  // 401. Cost: one fast API call per page-load; correctness gain:
  // unambiguous auth state.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    let cancelled = false;
    if (!user) setLoading(true);

    authApi.me()
      .then(res => {
        if (cancelled) return;
        const me = normalizeUser(res);
        localStorage.setItem(USER_KEY, JSON.stringify(me));
        setUser(me);
        initEcho();
        // Re-attest on session resume: the fingerprint survives in
        // localStorage but the Device row may have been revoked or pruned.
        attestDevice();
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        tearDownEcho();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    // Backend shape:  { token, user: {...}, roles: [...], permissions: [...] }
    const token = res?.token || res?.access_token;
    if (!token) throw new Error('Malformed login response.');
    const me = normalizeUser(res);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY,  JSON.stringify(me));
    setUser(me);
    initEcho();
    await attestDevice();
    return me;
  }, []);

  /**
   * Finalise the post-OTP session: the /auth/verify-otp response carries
   * a fresh token + role payload, identical to the login response, so we
   * persist it the same way.
   */
  const completeOtp = useCallback((res) => {
    const token = res?.token || res?.access_token;
    if (!token) throw new Error('Malformed verification response.');
    const me = normalizeUser(res);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY,  JSON.stringify(me));
    setUser(me);
    initEcho();
    attestDevice();
    return me;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore — local cleanup is what matters */ }
    tearDownEcho();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  /**
   * Rotate this session's token. The server mints a replacement and destroys
   * the one that made the call, so the new token MUST be persisted here — a
   * caller that fetched it and forgot to store it would be left holding a
   * credential the server has already revoked.
   */
  const refreshSession = useCallback(async () => {
    const res   = await authApi.refresh();
    const token = res?.token;
    if (!token) throw new Error('Malformed refresh response.');
    const me = normalizeUser({ ...res, user: res.user });
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(me));
    setUser(me);
    return me;
  }, []);

  /**
   * Revoke every token on the account, including the one in use. This
   * necessarily signs the current browser out too — that is the point of the
   * action, not a side effect, so the local session is torn down rather than
   * left pointing at a credential the server no longer honours.
   */
  const logoutAllSessions = useCallback(async () => {
    await authApi.logoutAll();
    tearDownEcho();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  /** Merge a server-confirmed profile change into the cached session. */
  const applyProfile = useCallback((patch) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = useMemo(() => {
    const roles       = user?.roles       ?? [];
    const permissions = user?.permissions ?? [];

    return {
      user,
      loading,
      login,
      logout,
      completeOtp,
      refreshSession,
      logoutAllSessions,
      applyProfile,
      isAuthenticated: !!user,
      roles,
      permissions,
      hasRole:    (...wanted) => wanted.some(r => roles.includes(r)),
      can:        permission   => permissions.includes(permission),
      homeFor:    ()           => homeForRoles(roles),
    };
  }, [user, loading, login, logout, completeOtp, refreshSession, logoutAllSessions, applyProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider.');
  return ctx;
}
